import React from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useLocation } from 'react-router-dom';
import { Card, Flex, Skeleton, Typography, theme } from 'antd';

import { useGetSearchPerformanceKpisQuery } from '../api/searchPerformanceApi';
import type { SearchPerformanceKpiCardName } from '../SearchPerformanceAnalyticsEvents';
import { buildPagesSectionHref, usePagesSectionNavigation, type PagesTabKey } from '../pages';
import { getReportingWindow } from '../SearchPerformanceReportingWindow';
import SearchPerformanceRequestError from '../SearchPerformanceRequestError';
import { toRequestLoad } from '../SearchPerformanceRequestLoad';
import useSearchPerformanceProperty from '../useSearchPerformanceProperty';
import useTrackSearchPerformanceEvent from '../useTrackSearchPerformanceEvent';
import useWidgetLoadTelemetry from '../useWidgetLoadTelemetry';
import KpiStat from './KpiStat';

const KPI_STRINGS = {
  title: 'KPIs',
  subtitle: 'Monitor your search performance',
  ofUrlsAnalyzed: 'of URLs analyzed',
  loadFailed: 'Unable to load these KPIs.',
  urlsAnalyzed: {
    label: 'Unique URLs analyzed',
    info: 'Every URL that Prerender or Google Search Console saw in this period, counted once — even if both saw it. Slightly different forms of the same URL, like a trailing slash or a tracking parameter, count as one.',
  },
  crawledVisible: {
    label: 'Crawled, with impressions',
    info: 'Pages Prerender served to Googlebot that appeared in Google search results at least once during the period. It does not mean they are still appearing now — a page can be indexed early in the window and dropped later.',
    cta: 'See pages with impressions',
  },
  crawledInvisible: {
    label: 'Crawled, no impressions',
    info: 'Pages Prerender served to Googlebot that never appeared in Google search results during the period. Google either has no record of them or chose not to show them, so this is the bucket to work through.',
    cta: 'See pages with no impressions',
  },
  visibleNotServed: {
    label: 'Not Prerender-served, with impressions',
    // Load-bearing hedge — the data only proves no crawl was observed in the
    // window. Wording constraint: AGENTS.md › "Bucket-3 wording is constrained".
    info: 'URLs that appeared in Google search results during the period, with no Prerender crawl observed for them in that same period. It does not mean Prerender never served them — a crawl before this window would look the same.',
    cta: 'See these pages',
  },
};

// A layout threshold, not a design value: the narrowest the longest label ("Not
// Prerender-served, with impressions") gets before needing a third line.
const MIN_TILE_WIDTH = 190;

/**
 * The "KPIs" partition card: one total split three ways, on one row. Ported from
 * the Moonshot prototype's `PartitionKpiCard` (SearchPerformancePage.tsx).
 *
 * Three devices carry "this is a sum": the total outranks the parts in type
 * size, a segmented bar inside the total splits by the parts' real counts, and
 * color chips tie each part's label to its segment in the same order. No
 * vertical dividers — the chips already delineate the parts, and a column gap
 * cannot break on wrap.
 */
function KpiSection() {
  const { token } = theme.useToken();

  // The hook (AGENTS.md property rule) both gates the query — the widget
  // never fires before the connection status resolves — and supplies the
  // property the BFF scopes the KPIs by.
  const { selectedProperty, isLoading: isPropertyLoading } = useSearchPerformanceProperty();
  const { dateStart, dateEnd } = getReportingWindow();
  // `currentData` rather than `data`: `data` retains the previous property's
  // figures while the new one loads, which reads as "nothing happened" on a
  // property switch. `currentData` is per-arg, so the switch drops the stale
  // figures and the skeleton shows — the same behaviour the path table gets
  // from its stale-data drop. Unlike the table, KPIs have no sort/page, so
  // there is no cross-arg state worth keeping.
  const kpisQuery = useGetSearchPerformanceKpisQuery(
    selectedProperty ? { dateStart, dateEnd, property: selectedProperty.propertyUrl } : skipToken
  );
  const { currentData, isError, isFetching, isUninitialized, refetch } = kpisQuery;
  const trackEvent = useTrackSearchPerformanceEvent();
  const { trackRetryClicked } = useWidgetLoadTelemetry({
    property: selectedProperty?.propertyUrl,
    reportingWindow: { dateStart, dateEnd },
    load: toRequestLoad(kpisQuery),
    loadedProperties: { widget: 'kpis', has_data: (currentData?.urlsAnalyzed ?? 0) > 0 },
    failedProperties: { widget: 'kpis' },
  });
  const { search } = useLocation();
  const navigateToPages = usePagesSectionNavigation();

  // The query-derived terms are gated on the query actually running: under
  // `skipToken` none of them ever resolve, so an ungated `!currentData && !isError`
  // leaves the skeleton up forever. `isUninitialized` rather than `isFetching`
  // for the reason useGscSyncState documents — the query is dispatched from an
  // effect, so on the render where the arg first appears it is merely
  // uninitialised and nothing is in flight yet.
  const isPending = isPropertyLoading || (!!selectedProperty && (isUninitialized || (isFetching && !currentData)));

  const handleRetry = () => {
    trackRetryClicked();
    refetch();
  };

  // Semantic rather than data-viz hues: the three buckets really are states —
  // working, needs attention, coverage gap. Fills and chips only, never text.
  const accents = [token.colorPrimary, token.colorWarning, token.colorInfo];

  // Rounded percents may not sum to exactly 100 — accepted, as in the prototype.
  const percentOfAnalyzed = (part: number) =>
    currentData && currentData.urlsAnalyzed > 0 ? Math.round((part / currentData.urlsAnalyzed) * 100) : 0;

  // Each bucket card's CTA lands on the Pages tab listing exactly the URLs it
  // counts — the tab filters reuse the KPI coverage-signal SQL upstream, so
  // the tab total matches the card by construction.
  const parts: {
    id: string;
    kpiCardName: SearchPerformanceKpiCardName;
    strings: { label: string; info: string; cta: string };
    count: number | null;
    pagesTab: PagesTabKey;
  }[] = [
    {
      id: 'kpi-crawled-impressions',
      kpiCardName: 'crawled_with_impressions',
      strings: KPI_STRINGS.crawledVisible,
      count: isError ? null : currentData?.crawledWithImpressions ?? 0,
      pagesTab: 'with_impressions',
    },
    {
      id: 'kpi-crawled-no-impressions',
      kpiCardName: 'crawled_no_impressions',
      strings: KPI_STRINGS.crawledInvisible,
      count: isError ? null : currentData?.crawledNoImpressions ?? 0,
      pagesTab: 'no_impressions',
    },
    {
      id: 'kpi-not-served-impressions',
      kpiCardName: 'not_prerender_served_with_impressions',
      strings: KPI_STRINGS.visibleNotServed,
      count: isError ? null : currentData?.notServedWithImpressions ?? 0,
      pagesTab: 'not_prerender_served',
    },
  ];

  return (
    <Flex vertical gap={token.marginXS}>
      <Flex vertical>
        <Typography.Title
          level={3}
          style={{
            margin: 0,
            fontSize: token.fontSizeHeading4,
            fontWeight: token.fontWeightStrong,
          }}
        >
          {KPI_STRINGS.title}
        </Typography.Title>
        <Typography.Text type="secondary" style={{ display: 'block', maxWidth: 640 }}>
          {KPI_STRINGS.subtitle}
        </Typography.Text>
      </Flex>

      {isError && <SearchPerformanceRequestError message={KPI_STRINGS.loadFailed} onRetry={handleRetry} />}

      <Card>
        {isPending ? (
          <Skeleton active paragraph={{ rows: 3 }} />
        ) : (
          /* Raw grid div is intentional: antd Flex/Row cannot express
             auto-fit minmax, which is what lets the tiles wrap without ever
             clipping. */
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fit, minmax(${MIN_TILE_WIDTH}px, 1fr))`,
              columnGap: token.sizeXL,
              rowGap: token.marginLG,
              alignItems: 'stretch',
            }}
          >
            <KpiStat
              id="kpi-unique-urls"
              label={KPI_STRINGS.urlsAnalyzed.label}
              info={KPI_STRINGS.urlsAnalyzed.info}
              value={isError ? null : currentData?.urlsAnalyzed ?? 0}
              valueFontSize={token.fontSizeHeading2}
              segments={parts.map((part, index) => ({
                color: accents[index],
                value: part.count ?? 0,
              }))}
            />
            {parts.map((part, index) => (
              <KpiStat
                key={part.id}
                id={part.id}
                label={part.strings.label}
                info={part.strings.info}
                value={part.count}
                accent={accents[index]}
                caption={`${percentOfAnalyzed(part.count ?? 0)}% ${KPI_STRINGS.ofUrlsAnalyzed}`}
                cta={{
                  label: part.strings.cta,
                  href: buildPagesSectionHref(search, { tab: part.pagesTab }),
                  onClick: (event) => {
                    event.preventDefault();
                    trackEvent('Search Performance: KPI Card Clicked', {
                      kpi_card_name: part.kpiCardName,
                      destination_tab: part.pagesTab,
                    });
                    navigateToPages({ tab: part.pagesTab });
                  },
                }}
              />
            ))}
          </div>
        )}
      </Card>
    </Flex>
  );
}

export default KpiSection;
